import type { FormInstance } from 'antd';
import { Form, Select } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';

import { MultipleDatePicker } from 'app/components/pages/Collections/CollectionDetail/CollectionSections/MultipleDatePicker/MultipleDatePicker';
import { HeaderWrapper } from 'app/components/pages/Edito/commons/Detail/HeaderWrapper/HeaderWrapper';
import type { IEditoEventForm, SectionExperienceType } from 'app/typings/edito';
import { requiredRule } from 'app/utils/rules';

import { FormLink } from '../FormLink/FormLink';

import './ExperienceTypeForm.scss';

type Props = {
  fieldName: string | number;
  form: FormInstance<IEditoEventForm>;
};

const experienceTypesOptions: DefaultOptionType[] = [
  {
    label: 'Experiences with night (staycations)',
    value: 'staycation',
  },
  {
    label: 'Experiences by day (moments)',
    value: 'moment',
  },
];

const nightCountOptions: DefaultOptionType[] = [
  {
    label: '1 night',
    value: 1,
  },
  {
    label: '2 nights',
    value: 2,
  },
];

const zeroNightCountOption: DefaultOptionType[] = [
  {
    label: '0 night',
    value: 0,
  },
];

export const ExperienceTypeForm = ({ fieldName, form }: Props) => {
  const isEndingBlock = fieldName === 'endingBlock';

  const basePathName = isEndingBlock
    ? [fieldName, 'searchFilters']
    : ['sections', fieldName, 'searchFilters'];

  const experienceType = Form.useWatch(
    [...basePathName, 'experienceType'],
    form
  );

  const nightCount = Form.useWatch([...basePathName, 'nightCount'], form);

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const onChangeExperienceType = (experienceType: SectionExperienceType) => {
    form.setFieldValue(
      [...basePathName, 'nightCount'],
      experienceType === 'moment' ? 0 : 1
    );
  };

  const onChangeNightCount = (newNightCount: 1 | 2) => {
    if (newNightCount === 1) {
      form.setFieldValue([...basePathName, 'dates'], undefined);
    }
  };

  return (
    <HeaderWrapper title="Type" gapTitleChildren={16}>
      <div className="experienceTypeContainer">
        <div className="experienceTypeSubContainer">
          <HeaderWrapper isBold={false} title="Experience type">
            <Form.Item
              name={[fieldName, 'searchFilters', 'experienceType']}
              rules={[requiredRule]}
            >
              <Select
                onChange={onChangeExperienceType}
                options={experienceTypesOptions}
                placeholder="Select experience type"
              />
            </Form.Item>
          </HeaderWrapper>
        </div>
        <div className="experienceTypeSubContainer">
          <div className="staycationContainer">
            <div className="nightCountContainer">
              <FormLink />
              <HeaderWrapper isBold={false} title="Night count selected">
                <Form.Item
                  name={[fieldName, 'searchFilters', 'nightCount']}
                  rules={[requiredRule]}
                >
                  <Select
                    onChange={onChangeNightCount}
                    placeholder="Select night count"
                    options={
                      experienceType === 'staycation'
                        ? nightCountOptions
                        : zeroNightCountOption
                    }
                    disabled={experienceType !== 'staycation'}
                  />
                </Form.Item>
              </HeaderWrapper>
            </div>
            {nightCount === 2 && (
              <>
                <div className="multiNightLink" />
                <div className="datesMultiNightContainer">
                  <FormLink width={12} />
                  <HeaderWrapper isBold={false} title="Dates">
                    <Form.Item
                      name={[fieldName, 'searchFilters', 'dates']}
                      rules={[requiredRule]}
                    >
                      <MultipleDatePicker />
                    </Form.Item>
                  </HeaderWrapper>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </HeaderWrapper>
  );
};
